import ApiService from 'services/ApiService/ApiService';
import { TypeOfValuesOf } from 'utils/typeHelpers';


/**
 * @readonly
 * @enum {string}
 */
export enum ConfigurationKeys {
  ACTIVE_GAME_SERVER = 'activeGameServer',
  ACTIVE_LANGUAGE = 'activeLanguage',
  ADMIN_APPROVAL_SERVER = 'adminApprovalServer',
  ANNOUNCEMENTS_API = 'announcementsApi',
  ANNOUNCEMENTS_SERVER = 'announcementsServer',
  APM_REAL_USER_MONITORING_BACKEND_URL = 'apmRealUserMonitoringBackendUrl',
  APM_REAL_USER_MONITORING_SCRIPT_URL = 'apmRealUserMonitoringScriptUrl',
  AUTO_PAUSE_DURATION_MILLISECONDS = 'autoPauseDurationMillis',
  BASE_URL = 'baseUrl',
  BOOST_DURATION_MINUTES = 'boostDurationMinutes',
  BOOST_EXTENDED_DURATION_MINUTES = 'boostExtendedDurationMinutes',
  BOOST_FREE_PERIOD_MONTHS = 'boostFreePeriodMonths',
  BOOST_PRICE_TOKENS = 'boostPriceTokens',
  BOOST_VIEWER_THRESHOLD = 'boostViewerThreshold',
  BOT = 'bot',
  CAM4_ADMIN_BASE_URL = 'cam4adminBaseUrl',
  EPAY_SERVER = 'epayServer',
  EPAY_TENANT = 'epayApiTenant',
  EWALLET_SERVER = 'ewalletServer',
  GALLERIES_NAME_SIZE = 'galleriesNameSize',
  GALLERIES_TOTAL_COUNT = 'galleriesTotalCount',
  I18N = 'i18n',
  IMAGE_MODERATION_SERVER = 'imageModerationServer',
  INTERCOM_APP_ID = 'intercomAppId',
  LOCALE = 'locale',
  MAX_DIRECTORY_TAGS_COUNT = 'maxDirectoryTagsCount',
  MAX_DIRECTORY_TAGS_LENTH = 'maxDirectoryTagsLength',
  PASSWORD_STRENGTH_INCREASED = 'passwordStrengthIncreased',
  PAYOUT_SERVER = 'payoutServer',
  STREAMING_API_BASE_URL = 'streamingApiBaseUrl',
  TIPPING_BASE_URL = 'tippingBaseUrl',
  TEMPTED_BASE_URL = 'temptedBaseUrl',
  TRACE_ERRORS = 'traceErrors',
  TRANSLATIONS = 'translations',
  UI_VERSION = 'uiVersion',
  VWO_ACCOUNT_ID = 'vwoAccountID',
  WEB_CDN_LOCATION = 'webCdnLocation',
  WEB_CHAT_HOST = 'webchatHost',
  ACHIEVEMENTS_BASE_URL = 'achievementsBaseUrl',
  EMAIL_DOMAIN_TYPO_URL = 'emailDomainTypoUrl',
  ACHIEVEMENTS_POLLING_INTERVAL_MILLISECONDS = 'achievementsPollingIntervalMilliseconds',
  TRANSLATOR_URL = 'translatorUrl',
}

type Defaults = {
  maxDirectoryTagsLength: number;
  announcementsServer: string;
  bot: boolean;
  maxDirectoryTagsCount: number;
  locale: string;
  vwoAccountID: number;
  imageModerationServer: string;
  apmRealUserMonitoringBackendUrl: string;
  achievementsBaseUrl: string;
  webchatHost: string;
  translations: {};
  tippingBaseUrl: string;
  ewalletServer: string;
  activeGameServer: string;
  autoPauseDurationMillis: number;
  temptedBaseUrl: string;
  galleriesNameSize: number;
  jinkCdnLocation: string;
  galleriesTotalCount: number;
  epayPluginLocation: string;
  intercomAppId: string;
  passwordStrengthIncreased: string;
  boostDurationMinutes: number;
  boostViewerThreshold: number;
  webCdnLocation: string;
  uiVersion: string;
  boostPriceTokens: number;
  boostExtendedDurationMinutes: number;
  i18n: string;
  payoutServer: string;
  baseUrl: string;
  epayServer: string;
  epayApiTenant: string;
  activeLanguage: string;
  traceErrors: boolean;
  apmRealUserMonitoringScriptUrl: string;
  adminApprovalServer: string;
  emailDomainTypoUrl: string;
  achievementsPollingIntervalMilliseconds: number;
  translatorUrl: string;
}

type Configuration = Partial<Defaults>
type ConfigurationValues = TypeOfValuesOf<Defaults>

class ConfigurationService {
  /** Final merged configuration variables */
  configuration: Configuration;
  /** Build time variables defaults */
  defaults: Defaults | {};
  /** Build time variables overrides */
  overrides;

  constructor() {
    this.defaults = process.env.DEFAULT_WEBPACK_CONFIG as unknown as Defaults || {};
    this.overrides = process.env.WEBPACK_CONFIG || {};
    this.configuration = {
      baseUrl: window.BASE_URL || process.env.WEBPACK_BASE_URL || '',
      ...this.defaults,
    };
  }

  async initDev() {
    if (process.env.NODE_ENV === 'development') {
      const response = await ApiService.get('/dev-config');
      this.configuration = { ...this.configuration, ...response.data };
    }
  }

  async init() {
    await this.initDev();
    // Set webpack public path to the build time default in case the ui variables call fails
    // eslint-disable-next-line no-undef, camelcase
    __webpack_public_path__ = this.configuration.webCdnLocation || '';

    const url = `${this.configuration.baseUrl}/rest/v1.0/ui-variables`;
    const response = await ApiService.get(encodeURI(url));

    this.configuration = { ...this.configuration, ...response.data };
    this.configuration.translations = JSON.parse(this.configuration.translations as unknown as string || '{}');
    if (!this.configuration.i18n) {
      this.configuration.i18n = this.configuration.webCdnLocation;
    }

    this.configuration = { ...this.configuration, ...this.overrides };

    window.dataLayer = [];
    window.PASSWORD_STRENGTH_INCREASED = this.configuration.passwordStrengthIncreased;
    window.LOCALE = this.configuration.locale;
    window.TRANSLATIONS = this.configuration.translations;
    window.UI_TRACE_ERRORS = this.configuration.traceErrors;
    // DS-1225
    window.VWO_ACCOUNT_ID = window.VWO_ACCOUNT_ID || this.configuration.vwoAccountID.toString();
    window.IS_BOT = this.configuration.bot;
    window.BOOSTED_ROOM_VIEWER_THRESHOLD = this.configuration.boostViewerThreshold;
    window.BOOSTED_ROOM_PRICE_TOKENS = this.configuration.boostPriceTokens;
    window.BOOSTED_ROOM_EXTENDED_DURATION_MINUTES = this.configuration.boostExtendedDurationMinutes;
    window.BOOSTED_ROOM_DURATION_MINUTES = this.configuration.boostDurationMinutes;

    // eslint-disable-next-line no-undef, camelcase
    __webpack_public_path__ = this.configuration.webCdnLocation;

    // eslint-disable-next-line no-console
    console.log(`Configuration loaded: ${this.configuration.uiVersion}`);
  }

  /**
   * Get a configuration variable
   * @param { ConfigurationKeys } variable Variable key
   * @returns { string } Returns the variable or empty string if it doesn't exist
   */
  get(variable: keyof Configuration): ConfigurationValues | string {
    let defaultValue = '';
    if (variable === ConfigurationKeys.BASE_URL) {
      defaultValue = window.location.origin;
    }

    if (variable === ConfigurationKeys.EPAY_TENANT) {
      defaultValue = 'cam4';
    }

    return this.configuration[variable] || defaultValue;
  }
}

export default new ConfigurationService();
